import { FC, useMemo } from 'react'
import { Layer, Source } from 'react-map-gl/mapbox'
import type { FeatureCollection, Polygon as GeoJsonPolygon } from 'geojson'

export type AreaPolygonsRenderOptions = {
  fillColor?: string
  fillOpacity?: number
  fillHoverOpacity?: number
  lineColor?: string
  lineWidth?: number
  lineHoverColor?: string
  sourceId?: string
  fillLayerId?: string
  lineLayerId?: string
  highlightedId?: string | null
  idProperty?: string
}

type AreaPolygonsProps = {
  data: FeatureCollection<GeoJsonPolygon>
  options?: AreaPolygonsRenderOptions
}

const DEFAULTS = {
  fillColor: '#fa5252',
  fillOpacity: 0.25,
  fillHoverOpacity: 0.45,
  lineColor: '#c92a2a',
  lineWidth: 2,
  lineHoverColor: '#2f9e44',
  sourceId: 'area-polygons',
  fillLayerId: 'area-polygons-fill',
  lineLayerId: 'area-polygons-line',
  idProperty: 'areaId',
} as const

export const AreaPolygons: FC<AreaPolygonsProps> = ({ data, options }) => {
  const opts = { ...DEFAULTS, ...options }

  const paintData = useMemo(() => {
    if (!opts.highlightedId) return data
    return {
      ...data,
      features: data.features.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          highlighted: feature.properties?.[opts.idProperty] === opts.highlightedId,
        },
      })),
    }
  }, [data, opts.highlightedId, opts.idProperty])

  return (
    <Source id={opts.sourceId} type="geojson" data={paintData}>
      <Layer
        id={opts.fillLayerId}
        type="fill"
        paint={{
          'fill-color': ['case', ['boolean', ['get', 'highlighted'], false], opts.lineHoverColor, opts.fillColor],
          'fill-opacity': ['case', ['boolean', ['get', 'highlighted'], false], opts.fillHoverOpacity, opts.fillOpacity],
        }}
      />
      <Layer
        id={opts.lineLayerId}
        type="line"
        paint={{
          'line-color': ['case', ['boolean', ['get', 'highlighted'], false], opts.lineHoverColor, opts.lineColor],
          'line-width': opts.lineWidth,
        }}
      />
    </Source>
  )
}

export default AreaPolygons
